import { useEffect, useState, useCallback } from "react";
import { Pencil, Star, MapPin } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import AdminHeader from "../../components/admin/AdminHeader";
import SlidePanel from "../../components/admin/SlidePanel";
import { TextField, TextareaField, SelectField, FormFooterActions } from "../../components/admin/FormFields";
import ImageUpload from "../../components/admin/ImageUpload";
import VideoUpload from "../../components/admin/VideoUpload";

const EMPTY = {
  id: "",
  author: "",
  location: "Edmonton, AB",
  rating: 5,
  quote: "",
  project_type: "",
  avatar: "",
  video_url: "",
};

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/testimonials").then((r) => setItems(r.data || [])), []);
  useEffect(() => { load(); }, [load]);

  const open = (item) => setEditing(item ? { ...item } : { ...EMPTY });
  const close = () => setEditing(null);

  const save = async () => {
    if (!editing.author || !editing.quote) {
      toast.error("Author and Quote are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/testimonials", editing);
      toast.success("Review saved");
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing.id) return close();
    if (!window.confirm(`Delete review from "${editing.author}"?`)) return;
    try {
      await api.delete(`/admin/testimonials/${editing.id}`);
      toast.success("Review deleted");
      close();
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <AdminHeader
        title="Testimonials"
        subtitle="Customer reviews. Copy your best Google reviews here — they will appear in the Reviews block on Home and About pages."
        action="Add Review"
        actionTestId="add-testimonial-btn"
        onAction={() => open(null)}
        count={items.length}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {items.map((t) => (
          <button
            key={t.id}
            data-testid={`testimonial-row-${t.id}`}
            onClick={() => open(t)}
            className="group text-left bg-[#151515] border border-[#D8D6D0]/10 hover:border-[#D8D6D0]/30 transition-colors p-6"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex gap-0.5">
                {[0,1,2,3,4].map((i) => (
                  <Star key={i} className={`w-4 h-4 ${i < t.rating ? "fill-[#E2B85A] stroke-[#E2B85A]" : "stroke-[#D8D6D0]/30"}`} />
                ))}
              </div>
              <Pencil className="w-4 h-4 text-[#D8D6D0]/30 group-hover:text-[#F5F3EF]" />
            </div>
            <p className="text-sm text-[#D8D6D0]/80 italic line-clamp-4">"{t.quote}"</p>
            <div className="mt-4 pt-4 border-t border-[#D8D6D0]/10 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#0d0d0d] overflow-hidden flex-shrink-0">
                {t.avatar ? (
                  <img src={t.avatar.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${t.avatar}` : t.avatar} alt={t.author} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#D8D6D0]/40 text-xs font-serif-display">{t.author.charAt(0)}</div>
                )}
              </div>
              <div className="min-w-0">
                <div className="font-serif-display text-sm truncate">{t.author}</div>
                <div className="text-[10px] text-[#D8D6D0]/50 uppercase tracking-widest flex items-center gap-1.5">
                  <MapPin className="w-2.5 h-2.5" /> {t.location}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <SlidePanel
        open={!!editing}
        onClose={close}
        title={editing?.id ? "Edit Review" : "New Review"}
        subtitle={editing?.id ? "" : "Add a customer review (e.g. copied from your Google profile)"}
        footer={
          <FormFooterActions
            onCancel={close}
            onSubmit={save}
            onDelete={editing?.id ? remove : null}
            submitting={saving}
          />
        }
      >
        {editing && (
          <>
            <TextField id="author" label="Customer Name" value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} required testId="testimonial-author" placeholder="John Smith" />
            <TextField id="location" label="Location" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} placeholder="Edmonton, AB" />
            <SelectField
              id="rating"
              label="Rating"
              value={String(editing.rating)}
              onChange={(v) => setEditing({ ...editing, rating: Number(v) })}
              options={[5,4,3,2,1].map((n) => ({ value: String(n), label: `${n} ★ ${"★".repeat(n - 1)}` }))}
            />
            <TextareaField
              id="quote"
              label="Review Quote"
              value={editing.quote}
              onChange={(v) => setEditing({ ...editing, quote: v })}
              rows={5}
              required
              hint="Tip: paste 2-4 sentences from the most impactful part of the Google review."
            />
            <TextField id="project_type" label="Project Type" value={editing.project_type} onChange={(v) => setEditing({ ...editing, project_type: v })} placeholder="Outdoor Living, Paver Patio, etc." />
            <ImageUpload label="Avatar (optional)" value={editing.avatar} onChange={(v) => setEditing({ ...editing, avatar: v })} hint="Customer photo or initial circle. Square works best." />
            <VideoUpload
              label="Video Testimonial (optional)"
              value={editing.video_url}
              onChange={(v) => setEditing({ ...editing, video_url: v })}
              testId="testimonial-video"
              hint="Upload an MP4 directly, or paste a YouTube/Vimeo URL. When set, the video replaces the avatar on the public reviews block; the quote text is shown beneath it."
            />
          </>
        )}
      </SlidePanel>
    </div>
  );
}
